/**
 * Addons - Página de gerenciamento de add-ons disponíveis
 * CRUD completo de plan_addons para o sistema de cobrança avançado
 * Suporte a preços diferenciados por provider (ex: z-api vs waha)
 */

import { useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { Plus, Edit, Trash2, Package, Users, Inbox, MessageSquare, Megaphone, Layers, Link, LayoutGrid, ListChecks, HardDrive, Gift, Coins } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { showToast } from '@/lib/toast';
import { applyCurrencyMask, parseCurrency } from '@/lib/input-masks';
import { logger } from '@/lib/logger';

// Tipos de add-ons disponíveis
// NOTA: 'crm_module' é identificado pelo slug na lógica do webhook, 
// usa addon_type='module' no banco para evitar migration de enum
const ADDON_TYPES = [
  { value: 'agent', label: 'Agentes Adicionais', icon: Users },
  { value: 'inbox', label: 'Inboxes Adicionais', icon: Inbox },
  { value: 'channel', label: 'Canais Adicionais', icon: MessageSquare },
  { value: 'module', label: 'Módulo (ex: Campanhas)', icon: Megaphone },
  { value: 'crm_module', label: 'Módulo CRM', icon: LayoutGrid },
  { value: 'tasks_module', label: 'Módulo Tarefas', icon: ListChecks },
  // Add-ons do módulo de Tarefas
  { value: 'task_pack', label: 'Pacote de Tarefas', icon: ListChecks },
  { value: 'task_storage', label: 'Armazenamento de Tarefas', icon: HardDrive },
  // Add-ons do módulo de Indicação (Referral)
  { value: 'referral_tier', label: 'Plano de Indicação', icon: Gift },
  { value: 'referral_credits_pack', label: 'Pacote de Créditos de Indicação', icon: Coins },
] as const;

// Interface do add-on base
interface PlanAddon {
  id: string;
  slug: string;
  name: string;
  description: string | null;
  addon_type: 'agent' | 'inbox' | 'channel' | 'module' | 'crm_module' | 'tasks_module' | 'task_pack' | 'task_storage' | 'referral_tier' | 'referral_credits_pack';
  // Cotas específicas para add-ons do módulo de Tarefas
  task_quota: number | null;
  storage_quota_mb: number | null;
  // Cotas específicas para add-ons do módulo de Indicação
  max_referral_campaigns: number | null;
  max_referral_programs: number | null;
  max_referral_affiliates: number | null;
  referral_bonus_credits: number | null;
  conversion_credit_quota: number | null;
  price_monthly: number;
  price_yearly: number;
  setup_fee: number;
  has_setup_fee: boolean;
  unit_value: number;
  max_quantity: number | null;
  min_plan_required: string | null;
  available_plans: string[];
  sort_order: number;
  stripe_product_id: string | null;
  stripe_price_monthly_id: string | null;
  stripe_price_yearly_id: string | null;
  stripe_setup_price_id: string | null;
  is_active: boolean;
  providerPricesCount?: number; // Contagem de preços por provider
}

// Interface para provider
interface Provider {
  id: string;
  name: string;
  slug: string;
}

// Interface para preço por provider
interface ProviderPrice {
  id?: string;
  provider_id: string;
  provider_name: string;
  provider_slug: string;
  price_monthly: number;
  setup_fee: number;
  is_active: boolean;
  // Campos Stripe por provider
  stripe_price_monthly_id: string | null;
  stripe_setup_price_id: string | null;
}

const emptyAddon: Partial<PlanAddon> = {
  slug: '',
  name: '',
  description: '',
  addon_type: 'agent',
  task_quota: null,
  storage_quota_mb: null,
  max_referral_campaigns: null,
  max_referral_programs: null,
  max_referral_affiliates: null,
  referral_bonus_credits: null,
  conversion_credit_quota: null,
  price_monthly: 0,
  price_yearly: 0,
  setup_fee: 0,
  has_setup_fee: false,
  unit_value: 1,
  max_quantity: null,
  min_plan_required: null,
  available_plans: [],
  sort_order: 0,
  stripe_product_id: null,
  stripe_price_monthly_id: null,
  stripe_price_yearly_id: null,
  stripe_setup_price_id: null,
  is_active: true,
};

const formatPrice = (value: number | null) => {
  if (value === null || value === 0) return '-';
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
};

const AdminAddons = () => {
  const [addons, setAddons] = useState<PlanAddon[]>([]);
  const [plans, setPlans] = useState<{ id: string; name: string }[]>([]);
  const [providers, setProviders] = useState<Provider[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<PlanAddon | null>(null);
  const [formData, setFormData] = useState<Partial<PlanAddon>>(emptyAddon);
  const [providerPrices, setProviderPrices] = useState<ProviderPrice[]>([]);
  const [deleting, setDeleting] = useState<string | null>(null);

  useEffect(() => {
    fetchData();
  }, []);

  // Busca dados iniciais: add-ons, planos e providers
  const fetchData = async () => {
    try {
      const [addonsRes, plansRes, providersRes] = await Promise.all([
        supabase.from('plan_addons').select('*').order('addon_type').order('name'),
        supabase.from('subscription_plans').select('id, name').order('sort_order'),
        supabase.from('api_providers').select('id, name, slug').eq('is_active', true).order('name'),
      ]);

      if (addonsRes.error) throw addonsRes.error;
      if (plansRes.error) throw plansRes.error;
      if (providersRes.error) throw providersRes.error;

      // Buscar contagem de preços por provider para cada add-on
      const addonIds = (addonsRes.data || []).map(a => a.id);
      const { data: pricesCountData } = await supabase
        .from('addon_provider_prices')
        .select('addon_id')
        .in('addon_id', addonIds)
        .eq('is_active', true);
      
      // Montar mapa de contagem
      const pricesCountMap: Record<string, number> = {};
      (pricesCountData || []).forEach((p: { addon_id: string }) => {
        pricesCountMap[p.addon_id] = (pricesCountMap[p.addon_id] || 0) + 1;
      });

      // Mapeamento completo de todos os campos do banco
      setAddons(
        (addonsRes.data || []).map((a: any) => ({
          id: a.id,
          slug: a.slug,
          name: a.name,
          description: a.description,
          addon_type: a.addon_type,
          task_quota: a.task_quota ?? null,
          storage_quota_mb: a.storage_quota_mb ?? null,
          max_referral_campaigns: a.max_referral_campaigns ?? null,
          max_referral_programs: a.max_referral_programs ?? null,
          max_referral_affiliates: a.max_referral_affiliates ?? null,
          referral_bonus_credits: a.referral_bonus_credits ?? null,
          conversion_credit_quota: a.conversion_credit_quota ?? null,
          price_monthly: a.price_monthly,
          price_yearly: a.price_yearly ?? 0,
          setup_fee: a.setup_fee ?? 0,
          has_setup_fee: a.has_setup_fee ?? false,
          unit_value: a.unit_value,
          max_quantity: a.max_quantity,
          min_plan_required: a.min_plan_required,
          available_plans: Array.isArray(a.available_plans) ? a.available_plans : [],
          sort_order: a.sort_order ?? 0,
          stripe_product_id: a.stripe_product_id,
          stripe_price_monthly_id: a.stripe_price_monthly_id,
          stripe_price_yearly_id: a.stripe_price_yearly_id,
          stripe_setup_price_id: a.stripe_setup_price_id,
          is_active: a.is_active,
          providerPricesCount: pricesCountMap[a.id] || 0,
        }))
      );
      setPlans(plansRes.data || []);
      setProviders(providersRes.data || []);
    } catch (error) {
      logger.error('[Addons] Erro ao buscar dados:', error);
      showToast.error('Não foi possível carregar os dados');
    } finally {
      setLoading(false);
    }
  };

  // Abre dialog para criar novo add-on
  const openCreateDialog = () => {
    setEditingItem(null);
    setFormData(emptyAddon);
    // Inicializa preços por provider vazios
    setProviderPrices(providers.map(p => ({
      provider_id: p.id,
      provider_name: p.name,
      provider_slug: p.slug,
      price_monthly: 0,
      setup_fee: 0,
      is_active: false,
      stripe_price_monthly_id: null,
      stripe_setup_price_id: null,
    })));
    setDialogOpen(true);
  };

  // Abre dialog para editar add-on existente
  const openEditDialog = async (item: PlanAddon) => {
    setEditingItem(item);
    setFormData(item);
    
    // Buscar preços por provider existentes
    const { data: existingPrices } = await supabase
      .from('addon_provider_prices')
      .select('*')
      .eq('addon_id', item.id);
    
    // Montar array com todos providers (preenchidos ou vazios)
    const prices = providers.map(p => {
      const existing = existingPrices?.find((ep: any) => ep.provider_id === p.id);
      return {
        id: existing?.id,
        provider_id: p.id,
        provider_name: p.name,
        provider_slug: p.slug,
        price_monthly: existing?.price_monthly || 0,
        setup_fee: existing?.setup_fee || 0,
        is_active: existing?.is_active ?? false,
        stripe_price_monthly_id: existing?.stripe_price_monthly_id || null,
        stripe_setup_price_id: existing?.stripe_setup_price_id || null,
      };
    });
    setProviderPrices(prices);
    setDialogOpen(true);
  };

  // Atualiza preço de um provider específico
  const updateProviderPrice = (index: number, field: keyof ProviderPrice, value: any) => {
    setProviderPrices(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  // Salva add-on e preços por provider
  const handleSubmit = async () => {
    if (!formData.slug || !formData.name) {
      showToast.error('Preencha todos os campos obrigatórios');
      return;
    }

    try {
      // Payload com todos os campos e nomes corretos das colunas
      const payload = {
        slug: formData.slug.toLowerCase().replace(/\s+/g, '_'),
        name: formData.name,
        description: formData.description || null,
        addon_type: formData.addon_type,
        // Cotas do módulo de Tarefas (apenas relevantes para task_pack/task_storage)
        task_quota: formData.addon_type === 'task_pack' ? (formData.task_quota ?? null) : null,
        storage_quota_mb: (formData.addon_type === 'task_pack' || formData.addon_type === 'task_storage')
          ? (formData.storage_quota_mb ?? null)
          : null,
        // Cotas do módulo de Indicação (apenas relevantes para referral_tier/referral_credits_pack)
        max_referral_campaigns: formData.addon_type === 'referral_tier' ? (formData.max_referral_campaigns ?? null) : null,
        max_referral_programs: formData.addon_type === 'referral_tier' ? (formData.max_referral_programs ?? null) : null,
        max_referral_affiliates: formData.addon_type === 'referral_tier' ? (formData.max_referral_affiliates ?? null) : null,
        referral_bonus_credits: formData.addon_type === 'referral_tier' ? (formData.referral_bonus_credits ?? 0) : 0,
        // Coluna NOT NULL DEFAULT 0 — usar 0 quando não for pack de créditos para respeitar a constraint
        conversion_credit_quota: formData.addon_type === 'referral_credits_pack' ? (formData.conversion_credit_quota ?? 0) : 0,
        price_monthly: formData.price_monthly || 0,
        price_yearly: formData.price_yearly || 0,
        setup_fee: formData.setup_fee || 0,
        has_setup_fee: formData.has_setup_fee ?? false,
        unit_value: formData.unit_value || 1,
        max_quantity: formData.max_quantity,
        min_plan_required: formData.min_plan_required || null,
        available_plans: formData.available_plans || [],
        sort_order: formData.sort_order || 0,
        stripe_product_id: formData.stripe_product_id || null,
        stripe_price_monthly_id: formData.stripe_price_monthly_id || null,
        stripe_price_yearly_id: formData.stripe_price_yearly_id || null,
        stripe_setup_price_id: formData.stripe_setup_price_id || null,
        is_active: formData.is_active ?? true,
      };

      let addonId: string;

      if (editingItem) {
        const { error } = await supabase.from('plan_addons').update(payload).eq('id', editingItem.id);
        if (error) throw error;
        addonId = editingItem.id;
      } else {
        const { data, error } = await supabase.from('plan_addons').insert(payload).select('id').single();
        if (error) throw error;
        addonId = data.id;
      }

      // Salvar preços por provider (apenas para tipo inbox)
      if (formData.addon_type === 'inbox') {
        for (const pp of providerPrices) {
          if (pp.is_active && pp.price_monthly > 0) {
            // Upsert: insere ou atualiza - incluindo campos Stripe
            const { error } = await supabase.from('addon_provider_prices').upsert({
              addon_id: addonId,
              provider_id: pp.provider_id,
              price_monthly: pp.price_monthly,
              setup_fee: pp.setup_fee || 0,
              is_active: true,
              stripe_price_monthly_id: pp.stripe_price_monthly_id || null,
              stripe_setup_price_id: pp.stripe_setup_price_id || null,
            }, { onConflict: 'addon_id,provider_id' });
            if (error) logger.error('[Addons] Erro ao salvar preço por provider:', String(error.message));
          } else if (pp.id) {
            // Se desativou, marca is_active=false
            await supabase.from('addon_provider_prices')
              .update({ is_active: false })
              .eq('id', pp.id);
          }
        }
      }

      showToast.success(editingItem ? 'Add-on atualizado' : 'Add-on criado');
      setDialogOpen(false);
      fetchData();
    } catch (error) {
      logger.error('[Addons] Erro ao salvar add-on:', error);
      showToast.error('Não foi possível salvar o add-on');
    }
  };

  // Exclui add-on
  const handleDelete = async (id: string) => {
    if (!confirm('Tem certeza que deseja excluir este add-on?')) return;
    setDeleting(id);
    try {
      const { error } = await supabase.from('plan_addons').delete().eq('id', id);
      if (error) throw error;
      showToast.success('Add-on excluído');
      fetchData();
    } catch (error) {
      showToast.error('Não foi possível excluir.');
    } finally {
      setDeleting(null);
    }
  };

  // Alterna status ativo/inativo
  const toggleActive = async (item: PlanAddon) => {
    try {
      const { error } = await supabase.from('plan_addons').update({ is_active: !item.is_active }).eq('id', item.id);
      if (error) throw error;
      fetchData();
    } catch (error) {
      showToast.error('Erro ao alterar status');
    }
  };

  const getAddonIcon = (type: string) => ADDON_TYPES.find((t) => t.value === type)?.icon || Package;

  const togglePlanAvailable = (planId: string) => {
    setFormData((prev) => ({
      ...prev,
      available_plans: prev.available_plans?.includes(planId)
        ? prev.available_plans.filter((p) => p !== planId)
        : [...(prev.available_plans || []), planId],
    }));
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Add-ons</h1>
          <p className="text-muted-foreground">Gerencie os add-ons disponíveis para contratação</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button onClick={openCreateDialog}>
              <Plus className="h-4 w-4 mr-2" />
              Novo Add-on
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingItem ? 'Editar Add-on' : 'Novo Add-on'}</DialogTitle>
              <DialogDescription>Configure um add-on para contratação</DialogDescription>
            </DialogHeader>
            <div className="grid gap-4 py-4">
              {/* Campos básicos */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Slug *</Label>
                  <Input value={formData.slug || ''} onChange={(e) => setFormData({ ...formData, slug: e.target.value })} disabled={!!editingItem} />
                </div>
                <div className="space-y-2">
                  <Label>Nome *</Label>
                  <Input value={formData.name || ''} onChange={(e) => setFormData({ ...formData, name: e.target.value })} />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Descrição</Label>
                <Textarea value={formData.description || ''} onChange={(e) => setFormData({ ...formData, description: e.target.value })} />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Tipo *</Label>
                  <Select value={formData.addon_type} onValueChange={(v) => setFormData({ ...formData, addon_type: v as any })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {ADDON_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Qtd por Compra</Label>
                  <Input type="number" value={formData.unit_value || 1} onChange={(e) => setFormData({ ...formData, unit_value: parseInt(e.target.value) || 1 })} />
                </div>
              </div>

              {/* Cotas do Add-on - somente para tipos do módulo de Tarefas */}
              {(formData.addon_type === 'task_pack' || formData.addon_type === 'task_storage') && (
                <>
                  <Separator className="my-2" />
                  <div className="space-y-3">
                    <div className="flex items-center gap-2">
                      <ListChecks className="h-4 w-4 text-muted-foreground" />
                      <Label className="text-base font-semibold">Cotas do Add-on</Label>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      {formData.addon_type === 'task_pack'
                        ? 'Quantidade de tarefas/mês incluídas e armazenamento incluso neste pacote.'
                        : 'Quantidade de armazenamento extra (MB) por unidade contratada.'}
                    </p>
                    <div className="grid grid-cols-2 gap-4">
                      {formData.addon_type === 'task_pack' && (
                        <div className="space-y-2">
                          <Label>Tarefas/Mês incluídas *</Label>
                          <Input
                            type="number"
                            value={formData.task_quota ?? ''}
                            onChange={(e) => setFormData({ ...formData, task_quota: e.target.value ? parseInt(e.target.value) : null })}
                            placeholder="Ex: 1000"
                          />
                          <p className="text-xs text-muted-foreground">Substitui o limite base do plano (maior vence)</p>
                        </div>
                      )}
                      <div className="space-y-2">
                        <Label>
                          {formData.addon_type === 'task_pack' ? 'Storage incluso (MB)' : 'Storage extra por unidade (MB) *'}
                        </Label>
                        <Input
                          type="number"
                          value={formData.storage_quota_mb ?? ''}
                          onChange={(e) => setFormData({ ...formData, storage_quota_mb: e.target.value ? parseInt(e.target.value) : null })}
                          placeholder={formData.addon_type === 'task_pack' ? 'Ex: 5120 (5 GB)' : 'Ex: 10240 (10 GB)'}
                        />
                        <p className="text-xs text-muted-foreground">
                          {formData.addon_type === 'task_pack'
                            ? 'Acumula com storage base do plano'
                            : 'Multiplicado pela quantidade contratada'}
                        </p>
                      </div>
                    </div>
                  </div>
                </>
              )}

              {/* Cotas do Add-on - somente para tipos do módulo de Indicação */}
              {formData.addon_type === 'referral_tier' && (
                <>
                  <Separator className="my-2" />
                  <div className="space-y-3">
                    <div className="flex items-center gap-2">
                      <Gift className="h-4 w-4 text-muted-foreground" />
                      <Label className="text-base font-semibold">Cotas de Indicação</Label>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      Limites do plano de indicação. Deixe em branco para ilimitado.
                    </p>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Máx. Campanhas</Label>
                        <Input
                          type="number"
                          value={formData.max_referral_campaigns ?? ''}
                          onChange={(e) => setFormData({ ...formData, max_referral_campaigns: e.target.value ? parseInt(e.target.value) : null })}
                          placeholder="Ex: 5 (vazio = ilimitado)"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Máx. Programas</Label>
                        <Input
                          type="number"
                          value={formData.max_referral_programs ?? ''}
                          onChange={(e) => setFormData({ ...formData, max_referral_programs: e.target.value ? parseInt(e.target.value) : null })}
                          placeholder="Ex: 1 (vazio = ilimitado)"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Máx. Afiliados</Label>
                        <Input
                          type="number"
                          value={formData.max_referral_affiliates ?? ''}
                          onChange={(e) => setFormData({ ...formData, max_referral_affiliates: e.target.value ? parseInt(e.target.value) : null })}
                          placeholder="Ex: 1000 (vazio = ilimitado)"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Créditos Cortesia</Label>
                        <Input
                          type="number"
                          value={formData.referral_bonus_credits ?? ''}
                          onChange={(e) => setFormData({ ...formData, referral_bonus_credits: e.target.value ? parseInt(e.target.value) : 0 })}
                          placeholder="Ex: 20"
                        />
                        <p className="text-xs text-muted-foreground">Créditos liberados ao ativar este tier</p>
                      </div>
                    </div>
                  </div>
                </>
              )}

              {/* Cotas do Add-on - somente para Pacote de Créditos de Indicação */}
              {formData.addon_type === 'referral_credits_pack' && (
                <>
                  <Separator className="my-2" />
                  <div className="space-y-3">
                    <div className="flex items-center gap-2">
                      <Coins className="h-4 w-4 text-muted-foreground" />
                      <Label className="text-base font-semibold">Cotas do Pacote</Label>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Créditos por Compra *</Label>
                        <Input
                          type="number"
                          value={formData.conversion_credit_quota ?? ''}
                          onChange={(e) => setFormData({ ...formData, conversion_credit_quota: e.target.value ? parseInt(e.target.value) : null })}
                          placeholder="Ex: 100"
                        />
                        <p className="text-xs text-muted-foreground">Quantidade creditada por aquisição (one-time)</p>
                      </div>
                    </div>
                  </div>
                </>
              )}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Preço Base Mensal *</Label>
                  <Input value={formData.price_monthly ? applyCurrencyMask(formData.price_monthly) : ''} onChange={(e) => setFormData({ ...formData, price_monthly: parseCurrency(e.target.value) })} />
                </div>
                <div className="space-y-2">
                  <Label>Taxa de Setup Base</Label>
                  <Input value={formData.setup_fee ? applyCurrencyMask(formData.setup_fee) : ''} onChange={(e) => setFormData({ ...formData, setup_fee: parseCurrency(e.target.value) || 0 })} />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Planos Disponíveis</Label>
                <div className="flex flex-wrap gap-2">
                  {plans.map((plan) => (
                    <Badge key={plan.id} variant={formData.available_plans?.includes(plan.id) ? 'default' : 'outline'} className="cursor-pointer" onClick={() => togglePlanAvailable(plan.id)}>
                      {plan.name}
                    </Badge>
                  ))}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Switch checked={formData.is_active ?? true} onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })} />
                <Label>Ativo</Label>
              </div>

              {/* Seção de Integração Stripe */}
              <Separator className="my-2" />
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <Link className="h-4 w-4 text-muted-foreground" />
                  <Label className="text-base font-semibold">Integração Stripe</Label>
                </div>
                <p className="text-sm text-muted-foreground">
                  IDs do Stripe para processamento de pagamentos deste add-on.
                </p>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-xs text-muted-foreground">Product ID</Label>
                    <Input 
                      value={formData.stripe_product_id || ''} 
                      onChange={(e) => setFormData({ ...formData, stripe_product_id: e.target.value || null })}
                      placeholder="prod_..."
                      className="font-mono text-sm"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs text-muted-foreground">Price Mensal ID</Label>
                    <Input 
                      value={formData.stripe_price_monthly_id || ''} 
                      onChange={(e) => setFormData({ ...formData, stripe_price_monthly_id: e.target.value || null })}
                      placeholder="price_..."
                      className="font-mono text-sm"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-xs text-muted-foreground">Price Anual ID</Label>
                    <Input 
                      value={formData.stripe_price_yearly_id || ''} 
                      onChange={(e) => setFormData({ ...formData, stripe_price_yearly_id: e.target.value || null })}
                      placeholder="price_..."
                      className="font-mono text-sm"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs text-muted-foreground">Price Setup ID</Label>
                    <Input 
                      value={formData.stripe_setup_price_id || ''} 
                      onChange={(e) => setFormData({ ...formData, stripe_setup_price_id: e.target.value || null })}
                      placeholder="price_..."
                      className="font-mono text-sm"
                    />
                  </div>
                </div>
              </div>

              {/* Seção de preços por provider - apenas para tipo inbox */}
              {formData.addon_type === 'inbox' && providers.length > 0 && (
                <>
                  <Separator className="my-2" />
                  <div className="space-y-4">
                    <div className="flex items-center gap-2">
                      <Layers className="h-4 w-4 text-muted-foreground" />
                      <Label className="text-base font-semibold">Preços por Provider</Label>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      Configure preços específicos para cada provider. Se não configurado, será usado o preço base acima.
                    </p>
                    
                    <div className="space-y-3">
                      {providerPrices.map((pp, idx) => (
                        <div 
                          key={pp.provider_id} 
                          className={`flex items-center gap-4 p-3 border rounded-lg transition-colors ${
                            pp.is_active ? 'border-primary/30 bg-primary/5' : 'border-border'
                          }`}
                        >
                          <div className="flex items-center gap-2 w-36">
                            <Switch 
                              checked={pp.is_active}
                              onCheckedChange={(checked) => updateProviderPrice(idx, 'is_active', checked)}
                            />
                            <span className={`font-medium text-sm ${pp.is_active ? 'text-foreground' : 'text-muted-foreground'}`}>
                              {pp.provider_slug}
                            </span>
                          </div>
                          <div className="flex-1 grid grid-cols-2 gap-3">
                            <div className="space-y-1">
                              <Label className="text-xs text-muted-foreground">Preço Mensal</Label>
                              <Input 
                                value={pp.price_monthly ? applyCurrencyMask(pp.price_monthly) : ''}
                                onChange={(e) => updateProviderPrice(idx, 'price_monthly', parseCurrency(e.target.value))}
                                placeholder="R$ 0,00"
                                disabled={!pp.is_active}
                                className="h-8"
                              />
                            </div>
                            <div className="space-y-1">
                              <Label className="text-xs text-muted-foreground">Taxa Setup</Label>
                              <Input 
                                value={pp.setup_fee ? applyCurrencyMask(pp.setup_fee) : ''}
                                onChange={(e) => updateProviderPrice(idx, 'setup_fee', parseCurrency(e.target.value))}
                                placeholder="R$ 0,00"
                                disabled={!pp.is_active}
                                className="h-8"
                              />
                            </div>
                          </div>
                          {/* Campos Stripe por Provider */}
                          {pp.is_active && (
                            <div className="flex-1 grid grid-cols-2 gap-3 mt-2 pt-2 border-t border-dashed">
                              <div className="space-y-1">
                                <Label className="text-xs text-muted-foreground flex items-center gap-1">
                                  <Link className="h-3 w-3" />
                                  Stripe Price Mensal
                                </Label>
                                <Input 
                                  value={pp.stripe_price_monthly_id || ''}
                                  onChange={(e) => updateProviderPrice(idx, 'stripe_price_monthly_id', e.target.value || null)}
                                  placeholder="price_..."
                                  className="h-8 font-mono text-xs"
                                />
                              </div>
                              <div className="space-y-1">
                                <Label className="text-xs text-muted-foreground flex items-center gap-1">
                                  <Link className="h-3 w-3" />
                                  Stripe Price Setup
                                </Label>
                                <Input 
                                  value={pp.stripe_setup_price_id || ''}
                                  onChange={(e) => updateProviderPrice(idx, 'stripe_setup_price_id', e.target.value || null)}
                                  placeholder="price_..."
                                  className="h-8 font-mono text-xs"
                                />
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
              <Button onClick={handleSubmit}>{editingItem ? 'Salvar' : 'Criar'}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><Package className="h-5 w-5" />Add-ons</CardTitle>
          <CardDescription>{addons.length} add-ons cadastrados</CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? <p className="text-muted-foreground">Carregando...</p> : addons.length === 0 ? <p className="text-muted-foreground">Nenhum add-on</p> : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Add-on</TableHead>
                  <TableHead>Tipo</TableHead>
                  <TableHead className="text-right">Preço</TableHead>
                  <TableHead className="text-center">Qtd</TableHead>
                  <TableHead className="text-center">Cota</TableHead>
                  <TableHead className="text-center">Status</TableHead>
                  <TableHead>Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {addons.map((addon) => {
                  const Icon = getAddonIcon(addon.addon_type);
                  return (
                    <TableRow key={addon.id}>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Icon className="h-4 w-4" />
                          <div>
                            <p className="font-medium">{addon.name}</p>
                            <p className="text-xs text-muted-foreground">{addon.slug}</p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell><Badge variant="secondary">{ADDON_TYPES.find((t) => t.value === addon.addon_type)?.label}</Badge></TableCell>
                      <TableCell className="text-right">
                        <div className="flex flex-col items-end">
                          <span>{formatPrice(addon.price_monthly)}</span>
                          {addon.providerPricesCount && addon.providerPricesCount > 0 ? (
                            <Badge variant="outline" className="text-xs mt-1">
                              +{addon.providerPricesCount} providers
                            </Badge>
                          ) : null}
                        </div>
                      </TableCell>
                      <TableCell className="text-center">{addon.unit_value}</TableCell>
                      <TableCell className="text-center">
                        {addon.addon_type === 'task_pack' ? (
                          <div className="flex flex-col items-center text-xs">
                            <span className="font-medium">{addon.task_quota?.toLocaleString('pt-BR') ?? '-'} tarefas</span>
                            {addon.storage_quota_mb ? (
                              <span className="text-muted-foreground">+{(addon.storage_quota_mb / 1024).toFixed(0)} GB</span>
                            ) : null}
                          </div>
                        ) : addon.addon_type === 'task_storage' ? (
                          <span className="text-xs font-medium">
                            +{addon.storage_quota_mb ? (addon.storage_quota_mb / 1024).toFixed(0) : '-'} GB
                          </span>
                        ) : addon.addon_type === 'referral_tier' ? (
                          <div className="flex flex-col items-center text-xs leading-tight">
                            <span className="font-medium">
                              {addon.max_referral_campaigns ?? '∞'} camp · {addon.max_referral_programs ?? '∞'} prog
                            </span>
                            <span className="text-muted-foreground">
                              {addon.max_referral_affiliates?.toLocaleString('pt-BR') ?? '∞'} afil · {addon.referral_bonus_credits ?? 0} créd
                            </span>
                          </div>
                        ) : addon.addon_type === 'referral_credits_pack' ? (
                          <span className="text-xs font-medium">
                            +{addon.conversion_credit_quota ?? '-'} créditos
                          </span>
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </TableCell>
                      <TableCell className="text-center">
                        <Badge 
                          variant={addon.is_active ? 'default' : 'secondary'} 
                          className="cursor-pointer" 
                          onClick={() => toggleActive(addon)}
                        >
                          {addon.is_active ? 'Ativo' : 'Inativo'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-2">
                          <Button variant="ghost" size="icon" onClick={() => openEditDialog(addon)}>
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="icon" onClick={() => handleDelete(addon.id)} disabled={deleting === addon.id}>
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminAddons;
